import {Trans} from '@lingui/macro';
import React from 'react';

import {fontSizes} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {styled} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {openInBrowser} from '../../../../../../srcCommon/browser/openInBrowser';
import {AccordionSectionData} from '../../../../../components/common/accordion/accordionSection';
import {Emoji} from '../../../../../components/common/emoji/emoji';
import {emojiMap} from '../../../../../services/emojis/emojis';
import {RuleEditorSidebarLearnMore} from '../../ruleEditorSidebarLearnMore';
import {buildRuleEditorFaqSections, RuleEditorRecipeFaq} from '../helpers/ruleEditorRecipeFaq';

/*
 * Props.
 */

interface SlaEditorSidebarProps {}

/*
 * Styles.
 */

const StyledEmojiDiv = styled.div`
  display: inline-block;
  height: ${fontSizes.medium};
  position: relative;
  top: 1px;
`;

/*
 * Component.
 */

export const SlaEditorSidebar: React.FC<SlaEditorSidebarProps> = (props) => (
  <RuleEditorRecipeFaq sections={getSlaFaqSections()} />
);

/*
 * Helpers.
 */

function getSlaFaqSections(): ReadonlyArray<AccordionSectionData> {
  const sections = [
    {
      title: <Trans id="sla-editor-sidebar-item-1-title">What is an SLA?</Trans>,
      content: (
        <Trans id="sla-editor-sidebar-item-1-content">
          A service level agreement (SLA) is a target response time for customer conversations. For example,
          you might set an SLA to reply to customers within 1 hour during regular business hours. Front’s SLA
          rules ensure you meet that goal.
        </Trans>
      ),
    },
    {
      title: <Trans id="sla-editor-sidebar-item-2-title">What happens when an SLA is breached?</Trans>,
      content: (
        <>
          <Trans id="sla-editor-sidebar-item-2-content">
            If you don’t respond to a conversation in time, the tag{' '}
            <StyledEmojiDiv>
              <Emoji emoji={emojiMap.fire} />
            </StyledEmojiDiv>
            SLA breach will be added that conversation. You can track all current breaches with this tag and
            even display it in your workspace for quick access. You can also add other custom actions to
            escalate SLA breaches like moving the conversation or notifying the assignee.
          </Trans>
          <LearnMore />
        </>
      ),
    },
    {
      title: <Trans id="sla-editor-sidebar-item-3-title">Why set up a warning?</Trans>,
      content: (
        <>
          <Trans id="sla-editor-sidebar-item-3-content">
            Setting up an SLA warning allows you to avoid SLA breaches before they happen. The tag{' '}
            <StyledEmojiDiv>
              <Emoji emoji={emojiMap.warning} />
            </StyledEmojiDiv>
            ️ SLA warning will be added at the specified time to help your team see conversations at risk of
            an SLA breach, so they can reply ASAP.
          </Trans>
          <LearnMore />
        </>
      ),
    },
    {
      title: (
        <Trans id="sla-editor-sidebar-item-4-title">What happens if someone replies after a breach?</Trans>
      ),
      content: (
        <>
          <Trans id="sla-editor-sidebar-item-4-content">
            When someone replies to a conversation after an SLA breach, the{' '}
            <StyledEmojiDiv>
              <Emoji emoji={emojiMap.fire} />
            </StyledEmojiDiv>
            SLA breach tag is removed to reset the timer for the next response. You can still track past SLA
            breaches in analytics.
          </Trans>
          <LearnMore />
        </>
      ),
    },
    {
      title: (
        <Trans id="sla-editor-sidebar-item-5-title">What happens with SLA rules and business hours?</Trans>
      ),
      content: (
        <>
          <Trans id="sla-editor-sidebar-item-5-content">
            If you select “Within business hours” in Time limits, Front will only count time elapsed during
            business hours. With a 4 hour SLA limit and 8:00 a.m. – 5:00 p.m. business hours, a message
            arriving at 4:00 p.m. will breach its SLA the next day at 11:00 a.m.
          </Trans>
          <LearnMore />
        </>
      ),
    },
    {
      title: <Trans id="sla-editor-sidebar-item-6-title">How do I track SLA performance?</Trans>,
      content: (
        <>
          <Trans id="sla-editor-sidebar-item-6-content">
            You can track past breaches, warnings, and response time metrics using SLA reports in Front
            Analytics.
          </Trans>
          <LearnMore />
        </>
      ),
    },
  ];

  return buildRuleEditorFaqSections(sections);
}

const LearnMore = () => (
  <RuleEditorSidebarLearnMore onClick={() => openInBrowser('https://help.front.com/t/q6hh1bw/sla-rule')} />
);
